import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Img } from "components";
import Navbar from "components/Navbar/Index";
import Stepper from "components/Stepper/newstepper";
import { useSession } from "session";
import { ToggleSwitch } from "./ToggleSwitch";
import { InviteLinkPanel } from "pages/Applications/InviteLinkPanel";
import {
  linkButton,
  outlineButton as outline,
  primaryButton as primary,
} from "pages/ApplicationReview/styles";
import {
  BusinessApiError,
  checkPan,
  createBusiness,
  outcomeUnknown,
  FALLBACK_WORDS,
  listPortalProducts,
  type CreateBusinessResult,
  type PortalProduct,
} from "service/business";

const STEPS = ["PAN", "Contact", "Products"];
const PAN_FORMAT = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PAN_ERRORS = ["bad_pan_format", "pan_not_valid", "not_a_company_pan", "already_registered", "application_exists"];
const SEND_FAILED = "Couldn't send the invite. Nothing was saved. Try again.";
const SEND_UNKNOWN =
  "We couldn't confirm the invite was sent. Check Applications before trying again.";
/** The 4th character of a PAN says what kind of holder it is. Wording only; core decides what is accepted. */
const PAN_KINDS: Record<string, string> = {
  P: "an individual",
  F: "a firm or LLP",
  H: "a HUF",
  A: "an association of persons",
  T: "a trust",
  B: "a body of individuals",
  L: "a local authority",
  J: "an artificial juridical person",
  G: "a government body",
};
const notCompanyPan = (pan: string) =>
  `This PAN is for ${PAN_KINDS[pan.charAt(3)] ?? "an entity that is not a company"}. Only private and public limited companies can register for now.`;
const panErrorText = (code: string, message: string, pan: string) =>
  code === "not_a_company_pan" ? notCompanyPan(pan) : message;

const PAN_CHECK_FAILED = "Could not check the PAN. Try again.";
const PAN_RETURNED = "Fix the PAN and send again. Your other entries are kept.";

const cancelTrigger =
  "rounded-lg py-[11px] px-4 border bg-gray-button-gray-lite border-gray-border-gray-lite cursor-pointer hover:bg-[#E1EBFC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-700";
const rowBox =
  "flex items-center justify-between gap-3 border rounded-lg px-4 min-h-[56px] text-sm";

type FieldProps = {
  id: string;
  label: string;
  error?: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
};
function Field({ id, label, error, hint, children }: Readonly<FieldProps>) {
  return (
    <div className="flex flex-col gap-1.5 max-w-[470px]">
      <label htmlFor={id} className="text-sm font-medium text-[#333333]">
        {label} <span aria-hidden>*</span>
      </label>
      {children}
      {error ? (
        <span id={`${id}-error`} className="text-xs text-red-700" role="alert">
          {error}
        </span>
      ) : (
        hint && <span id={`${id}-hint`} className="text-xs text-[#6B7280]">{hint}</span>
      )}
    </div>
  );
}

/** Props every required input carries: border, aria-invalid, aria-describedby, aria-required. */
const fieldProps = (id: string, error?: string, hint?: boolean) => ({
  id,
  "aria-required": true as const,
  "aria-invalid": !!error,
  "aria-describedby": error ? `${id}-error` : hint ? `${id}-hint` : undefined,
  className: `field-focus-ring ${error ? "border-red-600" : ""}`,
});

export default function RegisterBusiness() {
  const navigate = useNavigate();
  const { can } = useSession();
  const [step, setStep] = useState(1);
  const [pan, setPan] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [creditDays, setCreditDays] = useState("0");
  const [editingCredit, setEditingCredit] = useState(false);
  const [creditEdited, setCreditEdited] = useState(false);
  const [needsLegalName, setNeedsLegalName] = useState(false);
  const [legalName, setLegalName] = useState("");
  // The PAN the check last passed for, and the name it returned (null when it was unavailable).
  const [checkedPan, setCheckedPan] = useState<string | null>(null);
  const [verifiedName, setVerifiedName] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const checkingRef = useRef(false);
  const sendingRef = useRef(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [panReturned, setPanReturned] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [result, setResult] = useState<CreateBusinessResult | null>(null);
  const [leaveOpen, setLeaveOpen] = useState(false);

  const [products, setProducts] = useState<PortalProduct[] | null>(null);
  const [productsFailed, setProductsFailed] = useState(false);
  const loadProducts = () => {
    setProducts(null);
    setProductsFailed(false);
    listPortalProducts()
      .then(setProducts)
      .catch(() => setProductsFailed(true));
  };
  useEffect(loadProducts, []);

  const panValue = pan.trim().toUpperCase();
  const clearError = (key: string) => setErrors((cur) => ({ ...cur, [key]: "" }));

  // A disabled button drops keyboard focus, so put it back where the rep continues.
  const focusLater = (id: string) =>
    setTimeout(() => document.getElementById(id)?.focus(), 0);

  const checkAndAdvance = async () => {
    if (checkingRef.current) return;
    checkingRef.current = true;
    setChecking(true);
    setErrors({});
    try {
      const res = await checkPan(panValue);
      setCheckedPan(panValue);
      setPanReturned(false);
      if (res.check === "verified" && res.legalName) {
        setVerifiedName(res.legalName);
        setNeedsLegalName(false);
        setLegalName("");
        setStep(2);
        focusLater("contact-name");
      } else {
        // No name came back: the rep types it, then presses Next again.
        setVerifiedName(null);
        setNeedsLegalName(true);
        focusLater("legal-name");
      }
    } catch (e) {
      const known = e instanceof BusinessApiError ? e : null;
      setErrors({
        pan:
          known && PAN_ERRORS.includes(known.code) && !outcomeUnknown(e)
            ? panErrorText(known.code, known.message, panValue)
            : PAN_CHECK_FAILED,
      });
      focusLater("pan");
    } finally {
      checkingRef.current = false;
      setChecking(false);
    }
  };

  const next = () => {
    if (step === 1) {
      if (checking) return;
      if (!PAN_FORMAT.test(panValue)) {
        setErrors({ pan: "PAN not valid. Check it and try again." });
        return;
      }
      if (checkedPan !== panValue) {
        void checkAndAdvance();
        return;
      }
      if (needsLegalName && !legalName.trim()) {
        setErrors({ legalName: "Enter the company's legal name." });
        return;
      }
      setErrors({});
      setPanReturned(false);
      setStep(2);
      return;
    }
    if (step === 2) {
      const e: Record<string, string> = {};
      if (!fullName.trim()) e.fullName = "Enter the contact person's name.";
      if (!EMAIL_FORMAT.test(email.trim())) e.email = "Enter a valid email.";
      if (!/^\d{10}$/.test(phone.replace(/\s/g, ""))) e.phone = "Enter a 10-digit mobile number.";
      setErrors(e);
      if (Object.keys(e).length === 0) setStep(3);
    }
  };

  const toggle = (slug: string, on: boolean) =>
    setSelected((cur) => (on ? [...cur, slug] : cur.filter((s) => s !== slug)));

  const creditValid = /^\d{1,3}$/.test(creditDays) && Number(creditDays) <= 365;

  const send = async () => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setSendError("");
    try {
      const res = await createBusiness({
        pan: panValue,
        ...(needsLegalName && legalName.trim() ? { legalName: legalName.trim() } : {}),
        contact: {
          fullName: fullName.trim(),
          email: email.trim(),
          phone: phone.replace(/\s/g, ""),
        },
        products: selected,
        // Only what the rep set; otherwise core applies its own default.
        ...(creditEdited ? { creditPeriodDays: Number(creditDays) } : {}),
      });
      setResult(res);
    } catch (e) {
      const known = e instanceof BusinessApiError ? e : null;
      const code = known?.code ?? "";
      if (code === "legal_name_required" && known) {
        setNeedsLegalName(true);
        setVerifiedName(null);
        setErrors({ pan: known.message });
        setPanReturned(true);
        setStep(1);
      } else if (PAN_ERRORS.includes(code) && known) {
        setCheckedPan(null);
        setVerifiedName(null);
        setErrors({ pan: panErrorText(code, known.message, panValue) });
        setPanReturned(true);
        setStep(1);
      } else if (code === "email_in_use" && known) {
        setErrors({ email: known.message });
        setStep(2);
      } else if (known && !outcomeUnknown(e) && known.message !== FALLBACK_WORDS) {
        // A documented refusal with its own wording (for example, a product no longer offered).
        setSendError(known.message);
      } else {
        setSendError(outcomeUnknown(e) ? SEND_UNKNOWN : SEND_FAILED);
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const reset = () => {
    setStep(1);
    setPan("");
    setFullName("");
    setEmail("");
    setPhone("");
    setSelected([]);
    setCreditDays("0");
    setCreditEdited(false);
    setNeedsLegalName(false);
    setLegalName("");
    setCheckedPan(null);
    setVerifiedName(null);
    setErrors({});
    setPanReturned(false);
    setSendError("");
    setResult(null);
  };

  if (!can("business.create")) {
    return (
      <main className="p-4 bg-gray-100 h-screen font-inter">
        <div className="bg-white-A700 p-5 rounded-xl text-sm">
          You do not have permission for this.{" "}
          <Link className={linkButton} to="/dashboard">
            Back to Home
          </Link>
        </div>
      </main>
    );
  }

  const dirty = Boolean(pan || fullName || email || phone || selected.length || legalName);
  const leave = () => navigate("/lead/contacts/corporate");
  const cancelButton = (
    <button
      type="button"
      className={cancelTrigger}
      onClick={() => (dirty ? setLeaveOpen(true) : leave())}
    >
      Cancel
    </button>
  );

  const companyName = verifiedName ?? legalName.trim();
  const checkNote = verifiedName ? "Checked" : "Name entered by you";

  const productsReady = products !== null && products.length > 0;
  const canSend = productsReady && selected.length > 0 && creditValid && !sending;
  let sendHint = "";
  if (!sending) {
    if (productsFailed) sendHint = "Load the products to continue.";
    else if (products !== null && products.length === 0) sendHint = "There are no products to send an invite for.";
    else if (productsReady && selected.length === 0) sendHint = "Switch on at least one product";
    else if (!creditValid) sendHint = "Enter a credit period from 0 to 365 days.";
  }

  return (
    <main className="p-4 bg-gray-100 flex flex-col min-h-screen font-inter gap-4 w-full">
      <Navbar>
        <div className="w-full flex justify-between items-center">
          <div className="flex gap-3 items-center">
            <Img src="images/sidebar_img/remit.svg" className="h-5" alt="" />
            <div className="gap-2 flex flex-col">
              <span className="text-sm font-semibold">Register a business</span>
              <span className="text-xs font-normal text-[#6B7280]">
                Start a business registration
              </span>
            </div>
          </div>
          {!result && cancelButton}
        </div>
      </Navbar>

      <div className="hidden sm:flex items-center justify-between bg-white-A700 rounded-xl px-4 py-3">
        <span className="text-sm font-semibold">Register a business</span>
        {!result && cancelButton}
      </div>

      <div className="bg-white-A700 p-5 rounded-xl flex flex-col gap-5 w-full flex-1">
        {result ? (
          <div className="flex flex-col gap-4 max-w-[560px]">
            <h1 className="text-xl font-semibold text-[#333333]">
              <span aria-hidden>✓ </span>Invite sent
            </h1>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-[#4B5160]">Company</span>
              <p className="text-lg font-semibold">{result.legalName}</p>
              <p className="text-sm text-[#6B7280]">
                PAN {panValue} · {checkNote}
              </p>
            </div>
            <p className="text-sm text-[#333333]">
              An invite went to {email.trim()}. Once they submit, it moves to
              Waiting for review.
            </p>
            {!result.emailSent && (
              <InviteLinkPanel name={fullName.trim()} url={result.inviteUrl} />
            )}
            <div className="flex gap-3">
              <button type="button" className={outline} onClick={reset}>
                Register another
              </button>
              <button
                type="button"
                className={primary}
                onClick={() => navigate("/lead/contacts/corporate?tab=applications")}
              >
                View applications
              </button>
            </div>
          </div>
        ) : (
          <>
            <Stepper steps={STEPS} currentStep={step - 1} showAllLabels />

            {step === 1 && (
              <div className="flex flex-col gap-4">
                <h1 className="text-lg font-semibold">Company PAN</h1>
                <p className="text-sm text-[#6B7280]">
                  The business finishes the rest after the invite.
                </p>
                {panReturned && (
                  <p className="text-sm border rounded-lg p-3 bg-[#FFF1D6] max-w-[470px]" role="status">
                    {PAN_RETURNED}
                  </p>
                )}
                <Field id="pan" label="Company PAN" error={errors.pan} hint={
                    verifiedName && checkedPan === panValue ? (
                      <span data-testid="pan-checked">
                        Checked · <span className="font-semibold text-[#333333]">{verifiedName}</span>
                      </span>
                    ) : (
                      "Checked when you press Next."
                    )
                  }>
                  <Input
                    {...fieldProps("pan", errors.pan, true)}
                    value={pan}
                    maxLength={10}
                    autoComplete="off"
                    autoCapitalize="characters"
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      // The name and the check belong to the PAN they were made for.
                      if (value !== pan) {
                        setNeedsLegalName(false);
                        setLegalName("");
                        setCheckedPan(null);
                        setVerifiedName(null);
                      }
                      setPan(value);
                      setErrors({});
                    }}
                    onKeyDown={(e) => e.key === "Enter" && next()}
                  />
                </Field>
                {needsLegalName && (
                  <Field id="legal-name" label="Legal name" error={errors.legalName}>
                    <Input
                      {...fieldProps("legal-name", errors.legalName)}
                      value={legalName}
                      autoComplete="off"
                      onChange={(e) => {
                        setLegalName(e.target.value);
                        clearError("legalName");
                      }}
                      onKeyDown={(e) => e.key === "Enter" && next()}
                    />
                  </Field>
                )}
              </div>
            )}

            {step >= 2 && (
              <div className="flex items-start justify-between gap-3 border rounded-lg px-4 py-3 max-w-[640px]" data-testid="company-summary">
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs text-[#4B5160]">Company</span>
                  <span className="text-sm font-semibold text-[#333333]">{companyName}</span>
                  <span className="text-xs text-[#6B7280]">PAN {panValue} · {checkNote}</span>
                </div>
                <button type="button" className={linkButton} onClick={() => setStep(1)} disabled={sending}>
                  Edit
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="flex flex-col gap-4">
                <h1 className="text-lg font-semibold">Who gets the invite?</h1>
                <Field id="contact-name" label="Contact person" error={errors.fullName}>
                  <Input
                    {...fieldProps("contact-name", errors.fullName)}
                    value={fullName}
                    autoComplete="off"
                    onChange={(e) => {
                      setFullName(e.target.value);
                      clearError("fullName");
                    }}
                  />
                </Field>
                <Field id="contact-email" label="Contact email" error={errors.email}>
                  <Input
                    {...fieldProps("contact-email", errors.email)}
                    type="email"
                    value={email}
                    autoComplete="off"
                    onChange={(e) => {
                      setEmail(e.target.value);
                      clearError("email");
                    }}
                  />
                </Field>
                <Field id="contact-mobile" label="Contact mobile" error={errors.phone}>
                  <Input
                    {...fieldProps("contact-mobile", errors.phone)}
                    type="tel"
                    inputMode="numeric"
                    value={phone}
                    autoComplete="off"
                    onChange={(e) => {
                      setPhone(e.target.value);
                      clearError("phone");
                    }}
                  />
                </Field>
              </div>
            )}

            {step === 3 && (
              <div className="flex flex-col gap-4 max-w-[640px]">
                <h1 className="text-lg font-semibold">Products</h1>
                <p className="text-sm text-[#6B7280]">
                  {fullName.trim()} · {email.trim()} · {phone.replace(/\s/g, "")}{" "}
                  <button type="button" className={linkButton} onClick={() => setStep(2)} disabled={sending}>
                    Edit
                  </button>
                </p>

                <div className="flex flex-col gap-3">
                  {products === null && !productsFailed && (
                    <p className="text-sm text-[#6B7280]">Loading products…</p>
                  )}
                  {productsFailed && (
                    <p className="text-sm" role="alert">
                      Couldn&apos;t load products.{" "}
                      <button type="button" className={linkButton} onClick={loadProducts}>
                        Retry
                      </button>
                    </p>
                  )}
                  {products !== null && products.length === 0 && (
                    <p className="text-sm">
                      No products are available for corporates yet. Switch a product on for corporates in Admin.
                    </p>
                  )}
                  {products?.map((p) => {
                    const on = selected.includes(p.slug);
                    return (
                      <div key={p.slug} className="flex items-center justify-between gap-4 border rounded-lg px-4 py-2 min-h-[56px]">
                        <label htmlFor={`product-${p.slug}`} className="flex flex-col gap-1 cursor-pointer">
                          <span className="text-sm font-semibold">{p.label}</span>
                          {p.note && <span className="text-xs text-[#6B7280]">{p.note}</span>}
                        </label>
                        <ToggleSwitch
                          id={`product-${p.slug}`}
                          checked={on}
                          disabled={sending}
                          onChange={(v) => toggle(p.slug, v)}
                        />
                      </div>
                    );
                  })}
                </div>

                <div className={rowBox}>Relationship Manager: you</div>

                <div className={rowBox}>
                  {editingCredit ? (
                    <span className="flex items-center gap-2 flex-wrap py-2">
                      <label htmlFor="credit-days">Credit period:</label>
                      <Input
                        id="credit-days"
                        className={`w-20 h-9 field-focus-ring ${creditValid ? "" : "border-red-600"}`}
                        inputMode="numeric"
                        value={creditDays}
                        aria-invalid={!creditValid}
                        aria-describedby={creditValid ? undefined : "credit-days-error"}
                        onChange={(e) => {
                          setCreditDays(e.target.value.replace(/\D/g, ""));
                          setCreditEdited(true);
                        }}
                      />
                      <span>days</span>
                      <button type="button" className={linkButton} disabled={!creditValid} onClick={() => setEditingCredit(false)}>
                        Done
                      </button>
                    </span>
                  ) : (
                    <>
                      <span className="flex flex-col py-2">
                        <span className="text-xs text-[#4B5160]">Payment terms</span>
                        <span>Credit period: {Number(creditDays)} days</span>
                      </span>
                      <button type="button" className={linkButton} onClick={() => setEditingCredit(true)} disabled={sending}>
                        Edit
                      </button>
                    </>
                  )}
                </div>
                {!creditValid && (
                  <p id="credit-days-error" className="text-xs text-red-700" role="alert">Enter a number of days from 0 to 365.</p>
                )}

                {sendError && (
                  <p className="text-sm text-red-700" role="alert">{sendError}</p>
                )}
              </div>
            )}

            <div className="flex gap-3 items-center flex-wrap">
              {step !== 1 && (
                <button type="button" className={outline} disabled={sending} onClick={() => setStep(step - 1)}>
                  Previous
                </button>
              )}
              {step < 3 ? (
                <button
                  type="button"
                  className={primary}
                  onClick={next}
                  disabled={checking}
                  aria-busy={checking}
                >
                  {checking ? "Checking PAN…" : "Next"}
                </button>
              ) : (
                <>
                  <button type="button" className={primary} disabled={!canSend} onClick={send}>
                    {sending ? "Sending invite…" : "Send invite"}
                  </button>
                  {sendHint && (
                    <span className="text-sm text-[#4B5160]">{sendHint}</span>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      <AlertDialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <AlertDialogPortal>
          <AlertDialogOverlay className=" bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
          <AlertDialogContent className="bg-white-A700 rounded-lg p-0 max-w-2xl animate-contentShow">
            <AlertDialogHeader className="flex text-left gap-4 pt-8 pb-4 px-10 ">
              <AlertDialogTitle className="font-bold text-xl leading-7 text-black-label">
                Leave without sending?
              </AlertDialogTitle>
              <AlertDialogDescription className="font-medium text-base leading-6 text-[#4B5160]">
                Nothing has been saved.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex justify-end items-center gap-2.5 py-5 px-10 flex-row rounded-b-lg bg-gray-button-gray-lite">
              <AlertDialogCancel
                className="m-0 rounded-lg border-blue-700 border py-[11px] px-4 text-blue-700 cursor-pointer hover:bg-[#E1EBFC] focus-visible:ring-2 focus-visible:ring-blue-700"
                asChild
              >
                <button type="button">Stay</button>
              </AlertDialogCancel>
              <AlertDialogAction
                className="rounded-lg border-blue-700 py-[11px] px-4 bg-blue-700 border text-white-A700 cursor-pointer hover:bg-blue-800 focus-visible:ring-2 focus-visible:ring-blue-700"
                asChild
              >
                <button type="button" onClick={leave}>
                  Leave
                </button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogPortal>
      </AlertDialog>
    </main>
  );
}
